import { Box, Grid, Typography, Paper, Button, Divider, useTheme, alpha, Skeleton } from '@mui/material'
import { Refresh as RefreshIcon, TrendingUp, Assessment, AutoGraph } from '@mui/icons-material'
import { useTranslation } from '@i18n'
import { useAuthStore } from '@store/authStore'
import { useFormStats } from '@hooks/useFormStats'
import { useAnalytics } from '@hooks/useAnalytics'
import StatsCards from '@components/Dashboard/StatsCards'
import RecentSubmissions from '@components/Dashboard/RecentSubmissions'
import SubmissionTrendsChart from '@components/Dashboard/SubmissionTrendsChart'
import StatusDistributionChart from '@components/Dashboard/StatusDistributionChart'
import DepartmentWorkloadChart from '@components/Dashboard/DepartmentWorkloadChart'
import FormPerformanceChart from '@components/Dashboard/FormPerformanceChart'
import Loading from '@components/Common/Loading'
import ReadPendingAlert from '@components/Dashboard/ReadPendingAlert'

const Dashboard = () => {
    const { t, language } = useTranslation()
    const { user } = useAuthStore()
    const { stats, isLoading: statsLoading, refetch: refetchStats } = useFormStats()
    const {
        trends,
        statusDistribution,
        departmentWorkload,
        formPerformance,
        isLoading: analyticsLoading,
        refetch: refetchAnalytics
    } = useAnalytics()
    const theme = useTheme()

    const getUserName = () => {
        if (language === 'ar' && user?.name_ar) {
            return user.name_ar
        }
        return user?.name_en || 'User'
    }

    // Check if user is admin or dashboard to show full analytics
    const showFullAnalytics = user?.user_type === 'admin' || user?.user_type === 'dashboard' || user?.user_type === 'form_manager'
    const showAdminCharts = user?.user_type === 'admin' || user?.user_type === 'dashboard'

    const handleRefresh = () => {
        refetchStats()
        if (showFullAnalytics) {
            refetchAnalytics()
        }
    }

    const isLoading = statsLoading || (showFullAnalytics && analyticsLoading)

    return (
        <Box sx={{ pb: 4 }}>
            {/* Read (Pending) Alert for form managers and dashboard users */}
            <ReadPendingAlert />
            {/* Hero Header Section */}
            <Paper
                elevation={0}
                sx={{
                    p: 4,
                    mb: 4,
                    borderRadius: 4,
                    background: theme.palette.mode === 'dark'
                        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.3)} 0%, ${alpha(theme.palette.secondary.dark, 0.2)} 100%)`
                        : `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                    border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                    position: 'relative',
                    overflow: 'hidden',
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        width: '40%',
                        height: '100%',
                        background: `radial-gradient(circle at 100% 0%, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 50%)`,
                        pointerEvents: 'none'
                    }
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        flexWrap: 'wrap',
                        gap: 2,
                        position: 'relative',
                        zIndex: 1
                    }}
                >
                    <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 3,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                                }}
                            >
                                <AutoGraph sx={{ color: 'white', fontSize: 28 }} />
                            </Box>
                            <Typography
                                variant="h4"
                                fontWeight={800}
                                sx={{
                                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                    backgroundClip: 'text',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent'
                                }}
                            >
                                {t('dashboard.welcome')}, {getUserName()} 👋
                            </Typography>
                        </Box>
                        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 500 }}>
                            {t('dashboard.overview')}
                        </Typography>
                    </Box>
                    <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={handleRefresh}
                        disabled={isLoading}
                        sx={{
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                            boxShadow: `0 4px 14px ${alpha(theme.palette.primary.main, 0.4)}`,
                            px: 3,
                            py: 1.2,
                            borderRadius: 2,
                            fontWeight: 600,
                            '&:hover': {
                                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.5)}`,
                                transform: 'translateY(-2px)'
                            },
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                    >
                        {t('common.refresh')}
                    </Button>
                </Box>
            </Paper>

            {/* Quick Stats Section */}
            <Box sx={{ mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box
                        sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: alpha(theme.palette.primary.main, 0.1)
                        }}
                    >
                        <Assessment sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                    </Box>
                    <Typography variant="h6" fontWeight={700}>
                        {t('dashboard.quickStats', 'Quick Statistics')}
                    </Typography>
                </Box>
                {statsLoading ? (
                    <Loading message={t('common.loading')} />
                ) : (
                    <StatsCards stats={stats} />
                )}
            </Box>

            {/* Charts Section */}
            {showFullAnalytics && (
                <>
                    <Divider
                        sx={{
                            my: 4,
                            borderColor: alpha(theme.palette.divider, 0.08)
                        }}
                    />

                    <Box sx={{ mb: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                            <Box
                                sx={{
                                    width: 36,
                                    height: 36,
                                    borderRadius: 2,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    bgcolor: alpha(theme.palette.secondary.main, 0.1)
                                }}
                            >
                                <TrendingUp sx={{ color: theme.palette.secondary.main, fontSize: 20 }} />
                            </Box>
                            <Typography variant="h6" fontWeight={700}>
                                {t('dashboard.analyticsOverview', 'Analytics Overview')}
                            </Typography>
                        </Box>

                        {/* Row 1: Trends and Status Distribution */}
                        <Grid container spacing={3} sx={{ mb: 3 }}>
                            <Grid item xs={12} lg={8}>
                                <SubmissionTrendsChart data={trends} isLoading={analyticsLoading} />
                            </Grid>
                            <Grid item xs={12} lg={4}>
                                <StatusDistributionChart data={statusDistribution} isLoading={analyticsLoading} />
                            </Grid>
                        </Grid>

                        {/* Row 2: Department Workload and Form Performance (admin/dashboard only) */}
                        {showAdminCharts && (
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <DepartmentWorkloadChart data={departmentWorkload} isLoading={analyticsLoading} />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormPerformanceChart data={formPerformance} isLoading={analyticsLoading} />
                                </Grid>
                            </Grid>
                        )}
                    </Box>
                </>
            )}

            {/* Recent Submissions Section */}
            <Divider
                sx={{
                    my: 4,
                    borderColor: alpha(theme.palette.divider, 0.08)
                }}
            />

            <Box>
                <Typography variant="h6" fontWeight={700} gutterBottom>
                    {t('dashboard.recentSubmissions')}
                </Typography>
                <RecentSubmissions />
            </Box>
        </Box>
    )
}

export default Dashboard
